import { defineStore } from 'pinia'
import { getAllowedRouteNames } from '../constants/navigation'
import { supabase } from '../supabase'

// Whitelist of routes that don't require authentication
const PUBLIC_ROUTES = ['success', 'entradaSalida', 'Encuesta', 'EncuestaExitosa']

export const useAuthStore = defineStore('chidos_auth', {
	state: () => ({
		session: null,
		userProfile: null,
		isInitialized: false
	}),

	persist: {
		key: 'chidos_store',
		storage: localStorage,
		paths: ['session', 'userProfile']
	},

	getters: {
		userType: (state) => state.userProfile?.user_type || null,
		isAdmin: (state) => state.userProfile?.user_type === 'admin',
		isOficina: (state) => state.userProfile?.user_type === 'oficina',
		isTienda: (state) => state.userProfile?.user_type === 'tienda',

		allowedRoutes: (state) => {
			const userType = state.userProfile?.user_type
			if (!userType) return []
			return getAllowedRouteNames(userType)
		}
	},

	actions: {
		setSession(session) {
			this.session = session
		},

		setUserProfile(profile) {
			this.userProfile = profile
		},

		clearAuth() {
			console.log('Clearing auth')
			this.session = null
			this.userProfile = null
		},

		async fetchUserProfile(userId) {
			console.log('Fetching user profile for:', userId)
			try {
				const { data, error } = await supabase
					.from('profiles')
					.select('*')
					.eq('user', userId)
					.single()

				if (error) {
					console.error('Error fetching user profile:', error)
					return null
				}

				console.log('User profile data:', data)
				this.setUserProfile(data)
				return data
			} catch (error) {
				console.error('Error in fetchUserProfile:', error)
				return null
			}
		},

		async login(email, password) {
			console.log('Attempting login for:', email)
			try {
				const { data, error } = await supabase.auth.signInWithPassword({
					email,
					password
				})

				if (error) {
					console.error('Login error:', error)
					throw error
				}

				console.log('Login successful, setting auth state')
				// Manually set session immediately
				this.setSession(data.session)

				// Fetch and set user profile
				if (data.session?.user?.id) {
					await this.fetchUserProfile(data.session.user.id)
				}

				return data
			} catch (error) {
				console.error('Login exception:', error)
				throw error
			}
		},

		async logout() {
			console.log('Logging out')
			try {
				// Sign out from Supabase
				await supabase.auth.signOut()

				// Manually clear auth state immediately
				this.clearAuth()
			} catch (error) {
				console.error('Logout error:', error)
				// Clear local state even if Supabase logout fails
				this.clearAuth()
			}
		},

		async checkExistingSession() {
			console.log('Checking for existing session')
			try {
				const { data, error } = await supabase.auth.getSession()

				if (error) {
					console.error('Error getting session:', error)
					this.clearAuth()
					return
				}

				if (data.session?.user?.id) {
					console.log('Found existing session, setting auth state')
					this.setSession(data.session)
					await this.fetchUserProfile(data.session.user.id)
				} else {
					console.log('No existing session found')
					this.clearAuth()
				}
			} catch (error) {
				console.error('Error checking existing session:', error)
				this.clearAuth()
			} finally {
				this.isInitialized = true
				console.log('Auth initialization complete')
			}
		},

		async refreshSession() {
			console.log('Refreshing session')
			try {
				const { data, error } = await supabase.auth.refreshSession()

				if (error) {
					console.error('Session refresh failed:', error)
					this.clearAuth()
					return false
				}

				if (data.session) {
					this.setSession(data.session)
					return true
				}

				return false
			} catch (error) {
				console.error('Session refresh error:', error)
				this.clearAuth()
				return false
			}
		},

		isRoutePublic(routeName) {
			console.log('Checking if route is public:', routeName)
			return PUBLIC_ROUTES.includes(routeName)
		},

		canAccessRoute(routeName) {
			const userType = this.userProfile?.user_type
			if (!userType) return false

			// Admin gets access to everything
			if (userType === 'admin') return true

			const allowedRoutes = getAllowedRouteNames(userType)
			return allowedRoutes.includes(routeName)
		},

		checkRouteAccess(route) {
			const routeName = route.name

			// First check if route is public
			if (this.isRoutePublic(routeName)) {
				return true
			}

			// If not public, user must be authenticated
			if (!this.session) {
				return false
			}

			// Check if user has permission for this route
			if (!this.canAccessRoute(routeName)) {
				return false
			}

			return true
		}
	}
})
